import {
  DEFAULT_LOOK,
  DIMENSION_KINDS,
  dimensionDefinition,
  layoutBounds,
  type DimensionLook,
} from '../dimensions';
import {
  bounds,
  center,
  chamfer,
  circleTouchingThree,
  closestPoint,
  corner,
  CornerError,
  dist,
  distanceTo,
  fillet,
  intersect,
  liesOn,
  linesTouching,
  midpoint,
  mirrorShape,
  offset,
  parameterOf,
  perpendicularFrom,
  piece,
  quadrants,
  tangentFrom,
  unionBounds,
  vec,
  type Bounds,
  type ChamferSize,
  type Shape,
  type Vec2,
} from '../geom';
import { ARROW_TYPES, DEFAULT_DISPLAY, type DimensionNode, type LengthDisplay } from '../model';
import { viewLayout } from '../views';
import { CALLS } from './api';

/** Thrown with a message the script sees as its own error. */
class Refusal extends Error {}

/**
 * Reads a request's numbers in order. They come from the sandbox, so each shape is rebuilt
 * from checked numbers, and running out or a bad kind code is a refusal.
 */
class Reader {
  private at = 0;

  constructor(
    private readonly numbers: readonly number[],
    private readonly display: LengthDisplay,
  ) {}

  number(): number {
    const value = this.numbers[this.at];
    this.at += 1;
    if (value === undefined || !Number.isFinite(value)) throw new Refusal('bad geometry request');
    return value;
  }

  point(): Vec2 {
    return vec(this.number(), this.number());
  }

  shape(): Shape {
    const call = CALLS[this.number()];
    if (!call) throw new Refusal('expected a Line, Circle or Arc');
    return call.decode(Array.from({ length: call.arity }, () => this.number()));
  }

  get done(): boolean {
    return this.at >= this.numbers.length;
  }

  /** A text height, arrow size and arrow index; -1 takes the default. */
  look(): DimensionLook {
    const [textHeight, arrowSize, arrow] = [0, 0, 0].map(() => this.number());
    return {
      textHeight: textHeight! > 0 ? textHeight! : DEFAULT_LOOK.textHeight,
      arrowSize: arrowSize! > 0 ? arrowSize! : DEFAULT_LOOK.arrowSize,
      arrow: ARROW_TYPES[arrow!] ?? DEFAULT_LOOK.arrow,
      display: this.display,
    };
  }

  points(): Vec2[] {
    const found: Vec2[] = [];
    while (this.at < this.numbers.length) found.push(this.point());
    return found;
  }
}

/** A shape as the sandbox reads it back: its kind code in `CALLS`, then its numbers. */
function encodeShape(shape: Shape): number[] {
  switch (shape.type) {
    case 'line':
      return [0, shape.a.x, shape.a.y, shape.b.x, shape.b.y];
    case 'circle':
      return [1, shape.center.x, shape.center.y, shape.radius];
    case 'arc':
      return [2, shape.center.x, shape.center.y, shape.radius, shape.start, shape.end];
  }
}

const encodePoints = (points: readonly Vec2[]): number[] => points.flatMap((p) => [p.x, p.y]);

const byParameter = (shape: Shape, points: Vec2[]): Vec2[] =>
  points
    .map((p) => ({ p, t: parameterOf(shape, p) }))
    .sort((a, b) => a.t - b.t)
    .map(({ p }) => p);

function split(shape: Shape, at: Vec2[]): Shape[] {
  if (shape.type === 'circle') {
    throw new Refusal('a circle cannot be split; draw the parts as arcs');
  }
  for (const p of at) {
    if (!liesOn(shape, p, 1e-6)) throw new Refusal('a split point is not on the shape');
  }
  const cuts = at.map((p) => parameterOf(shape, p)).sort((a, b) => a - b);
  const stops = [0, ...cuts, 1];
  return stops.slice(1).map((t, i) => piece(shape, stops[i]!, t));
}

/**
 * The point a corner edit was given, or, without one, the middle of the two shapes, which
 * sits in the corner of two shapes that meet like an L.
 */
function nearOf(read: Reader, a: Shape, b: Shape): Vec2 {
  if (read.number() === 1) return read.point();
  const [pa, pb] = [midpoint(a) ?? center(a), midpoint(b) ?? center(b)];
  return pa && pb ? vec((pa.x + pb.x) / 2, (pa.y + pb.y) / 2) : vec(0, 0);
}

function cornerEdit(make: () => readonly number[]): number[] {
  try {
    return [...make()];
  } catch (error) {
    if (error instanceof CornerError) throw new Refusal(error.message);
    throw error;
  }
}

function lineTouching(a: Shape, b: Shape, near: Vec2): Shape {
  if (a.type === 'line' || b.type === 'line') {
    throw new Refusal('Line.tangentTo needs two circles or arcs');
  }
  const found = linesTouching(a, b);
  if (found.length > 0) {
    return found.reduce((best, line) =>
      distanceTo(line, near) < distanceTo(best, near) ? line : best,
    );
  }
  const [ca, cb] = [center(a)!, center(b)!];
  if (dist(ca, cb) < Math.abs(a.radius - b.radius)) {
    throw new Refusal('no line touches both: one circle is inside the other');
  }
  throw new Refusal('no line touches both where the arcs are');
}

/** A shape's bounds, or a dimension's as its look lays it out; null when it cannot be drawn. */
function extentOf(read: Reader): Bounds | null {
  const code = read.number();
  const call = CALLS[code];
  if (call) return bounds(call.decode(Array.from({ length: call.arity }, () => read.number())));
  const kind = DIMENSION_KINDS[code - CALLS.length];
  if (kind === undefined) throw new Refusal('expected a shape or a dimension');
  const definition = dimensionDefinition(kind);
  const numbers = Array.from({ length: definition.arity }, () => read.number());
  const node = { type: 'dimension', ...definition.fromRecord(numbers, kind) } as DimensionNode;
  const measured = definition.resolve(node, read.look());
  return typeof measured === 'string' ? null : layoutBounds(measured.layout);
}

/** Longer titles are measured as this long, so a huge name cannot exhaust the host. */
const MAX_TITLE = 1000;

/** A view's frame, from its margin, title look and length, then what it holds. */
function viewFrame(read: Reader): number[] {
  const margin = read.number();
  const look = read.look();
  const title = 'x'.repeat(Math.min(Math.max(0, Math.round(read.number())), MAX_TITLE));
  const extents: Bounds[] = [];
  while (!read.done) {
    const extent = extentOf(read);
    if (extent) extents.push(extent);
  }
  if (extents.length === 0) throw new Refusal('the view has nothing drawn in it');
  const frame = layoutBounds(viewLayout(extents.reduce(unionBounds), margin, title, look));
  return [frame.min.x, frame.min.y, frame.max.x, frame.max.y];
}

const OPS: Readonly<Record<string, (read: Reader) => number[]>> = {
  viewFrame,
  midpoint: (read) => {
    const found = midpoint(read.shape());
    if (!found) throw new Refusal('a circle has no midpoint');
    return encodePoints([found]);
  },
  quadrants: (read) => encodePoints(quadrants(read.shape())),
  intersections: (read) => {
    const shape = read.shape();
    return encodePoints(byParameter(shape, intersect(shape, read.shape())));
  },
  nearest: (read) => encodePoints([closestPoint(read.shape(), read.point())]),
  perpendicularFrom: (read) => encodePoints(perpendicularFrom(read.shape(), read.point())),
  tangentsFrom: (read) => encodePoints(tangentFrom(read.shape(), read.point())),
  offset: (read) => {
    const copy = offset(read.shape(), read.number());
    if (!copy) throw new Refusal('no copy exists at that distance');
    return encodeShape(copy);
  },
  split: (read) => split(read.shape(), read.points()).flatMap(encodeShape),
  mirror: (read) => encodeShape(mirrorShape(read.shape(), read.point(), read.point())),
  corner: (read) => {
    const [a, b] = [read.shape(), read.shape()];
    const near = nearOf(read, a, b);
    return cornerEdit(() => {
      const met = corner(a, b, near);
      return [met.made.x, met.made.y, ...encodeShape(met.a), ...encodeShape(met.b)];
    });
  },
  fillet: (read) => {
    const [a, b, radius] = [read.shape(), read.shape(), read.number()];
    const near = nearOf(read, a, b);
    return cornerEdit(() => {
      const made = fillet(a, b, radius, near);
      return [...encodeShape(made.a), ...encodeShape(made.b), ...encodeShape(made.made)];
    });
  },
  chamfer: (read) => {
    const [a, b] = [read.shape(), read.shape()];
    const [byAngle, first, second] = [read.number(), read.number(), read.number()];
    const size: ChamferSize =
      byAngle === 1 ? { distance: first, angle: second } : { distances: [first, second] };
    const near = nearOf(read, a, b);
    return cornerEdit(() => {
      const made = chamfer(a, b, size, near);
      return [...encodeShape(made.a), ...encodeShape(made.b), ...encodeShape(made.made)];
    });
  },
  lineTouching: (read) => {
    const [a, b] = [read.shape(), read.shape()];
    return encodeShape(lineTouching(a, b, read.point()));
  },
  touchingThree: (read) => {
    const shapes = [read.shape(), read.shape(), read.shape()];
    const near = read.point();
    const [a, b, c] = shapes.map((shape) => ({ shape, point: closestPoint(shape, near) }));
    const found = circleTouchingThree(a!, b!, c!);
    if (!found) throw new Refusal('no circle touches all three shapes');
    return encodeShape(found);
  },
};

/**
 * Answers one geometry request from the sandbox: an operation and its numbers in, numbers
 * joined by commas out. A refusal comes back as `!` and its message for the script to throw,
 * so it points at the script's line; anything else thrown here is a bug and propagates.
 */
export function answerGeometry(
  op: string,
  numbers: readonly number[],
  display: LengthDisplay = DEFAULT_DISPLAY,
): string {
  try {
    const run = Object.hasOwn(OPS, op) ? OPS[op] : undefined;
    if (!run) throw new Refusal('unknown geometry request');
    return run(new Reader(numbers, display)).join(',');
  } catch (error) {
    if (error instanceof Refusal) return `!${error.message}`;
    throw error;
  }
}
